import * as React from 'react';
import * as _ from 'lodash';
import { Formik, Form } from 'formik';
import { Link } from 'react-router-dom';
import NotifyHOC from './HOC/Notifications';
import TextField from './TextField';
import AuthContext from './AuthContext';

import { User } from '../types/User';
import { getLoginValidationSchema } from '../utils/validation';
import { getFeaturedRapidSolution, loginProcess } from '../utils/authProcess';
import { getQueryString } from '@nexxt/common/services/TextService';
import { STATIC_STORAGE_URL } from '@nexxt/common/constants';
import Logo from '../assets/SVG/nexxt-logo';
import { FORGOT_PASSWORD, SIGNUP } from '../constants/routes';
import Warning from './Warning';
import Splash from './Splash';
import { SPLASH_CONTENT } from '../utils/constants';

interface LoginProps {
    toastNotification: any;
    user: User;
}

const LOGOUT_MESSAGE = 'Successfully logged out.';
const LOGOUT_MESSAGE_ERROR = 'Failed to logout.';
const LOGIN_MESSAGE_SUCCESS = 'Successfully logged in.';
const LOGIN_MESSAGE_ERROR = 'Wrong credentials. Please try again!';

const validateRedirectUrl = (refUrl: string): string => {
    const incaOrigins = [
        process.env.DASHBOARD_URL,
        process.env.PORTAL_URL,
        process.env.QUICKTAG_URL
    ];
    if (refUrl) {
        const urlObject = new URL(refUrl);
        if (incaOrigins.includes(urlObject.origin)) {
            return refUrl;
        }
    }
    return process.env.PORTAL_URL;
};

const Login = (props: LoginProps) => {
    const [textFocused, setTextFocused] = React.useState('log-in_wave');
    const { firebaseAuth } = React.useContext(AuthContext);
    const sourceApp = validateRedirectUrl(getQueryString('ref'));
    const rapidSolution = getFeaturedRapidSolution();
    const [splashIndex, setSplashIndex] = React.useState(
        Math.round(Math.random() * (SPLASH_CONTENT.length - 1))
    );

    const redirect = () => {
        const redirectTo = sourceApp
            ? sourceApp
            : process.env.PORTAL_URL + rapidSolution;
        window.location.href = redirectTo;
    };

    React.useEffect(() => {
        if (getQueryString('tokenExpired')) {
            props.toastNotification.error(LOGOUT_MESSAGE);
        }
    }, []);

    const handleSubmit = async (values: any) => {
        try {
            onFocusTextField('log-in_success');

            const { email, password } = values;
            const userCredential =
                await firebaseAuth.signInWithEmailAndPassword(email, password);

            await loginProcess(userCredential);

            props.toastNotification.success(LOGIN_MESSAGE_SUCCESS);
            redirect();
        } catch (error) {
            setTextFocused('log-in_failed');
            if (error.code === 'auth/too-many-requests') {
                props.toastNotification.error(error.message);
            } else {
                props.toastNotification.error(LOGIN_MESSAGE_ERROR);
            }
        }
    };

    const onFocusTextField = (txtFocused) => {
        setTextFocused(txtFocused);
    };

    const isLoggedIn = props.user;
    if (isLoggedIn && isLoggedIn.firstName) {
        redirect();
    }
    return (
        <div className="min-h-screen grid grid-cols-12">
            <div className="hidden lg:flex lg:col-span-6 xl:col-span-8 max-h-screen">
                <Splash index={splashIndex} />
            </div>
            <div className="col-span-12 lg:col-span-6 xl:col-span-4">
                <div className="auth--container login">
                    <div className="auth--wrapper">
                        <div className="auth--header flex flex-col items-center">
                            <Logo />
                            <p className="auth--header--title">Welcome back</p>
                            <p className="auth--header--subtitlt">
                                Let&apos;s get to work
                            </p>
                        </div>
                        <div className="auth--body">
                            <Formik
                                initialValues={{
                                    email: '',
                                    password: ''
                                }}
                                validationSchema={getLoginValidationSchema}
                                onSubmit={(values, actions) => {
                                    return handleSubmit(values);
                                }}
                                render={({
                                    values,
                                    touched,
                                    handleChange,
                                    handleBlur,
                                    errors
                                }) => (
                                    <Form className="login--form">
                                        <TextField
                                            type="text"
                                            name="email"
                                            className="form-group__control-md"
                                            value={values.email}
                                            onFocus={() =>
                                                onFocusTextField('log-in_email')
                                            }
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="E-mail"
                                            label="E-mail"
                                            errorMessage={errors.email}
                                            touchedValue={touched.email}
                                        />
                                        <TextField
                                            type="password"
                                            name="password"
                                            className="form-group__control-md"
                                            value={values.password}
                                            onFocus={() =>
                                                onFocusTextField(
                                                    'log-in_password_idle'
                                                )
                                            }
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Password"
                                            label="Password"
                                            errorMessage={errors.password}
                                            touchedValue={touched.password}
                                        />
                                        <div className="form-group form-link">
                                            <Link
                                                to={FORGOT_PASSWORD}
                                                className="form-link"
                                            >
                                                Forgot password?
                                            </Link>
                                        </div>
                                        <button
                                            disabled={!_.isEmpty(errors)}
                                            onFocus={() =>
                                                onFocusTextField(
                                                    'log-in_success'
                                                )
                                            }
                                            type="submit"
                                            className="form-submit__button"
                                        >
                                            Login
                                        </button>
                                        {/* <div className="form-group form-link mt-4 text-center">
                                            <Link
                                                to={SIGNUP + rapidSolution}
                                                className="form-link"
                                            >
                                                {`Don't have an account? Sign up.`}
                                            </Link>
                                        </div> */}
                                        <div className="sm:block lg:hidden">
                                            <Warning />
                                        </div>
                                    </Form>
                                )}
                            />
                        </div>
                    </div>
                </div>
                <div className="page--byline hidden lg:block">
                    <a
                        className="by-logo"
                        href="https://nexxt.in"
                        target="_blank"
                        rel="noreferrer"
                    >
                        <img
                            src={`${STATIC_STORAGE_URL}/img/powered_by_nexxt_intelligence_inca.png`}
                        />
                    </a>
                </div>
            </div>
        </div>
    );
};

export default NotifyHOC(Login);
